<div class="container-fluid">
    <h1 class="m-3 display-6 vm-container-header">
        <i class="bi bi-file-earmark-ruled"></i>
        EDI {{ this.fileType }} File Summary
    </h1>

    <div class="m-3 p-2 bg-body-light rounded-3 shadow">
        <div class="m-2">
            <form>
                <div class="mb-3 row align-items-center">
                    <div class="col-sm-auto">
                        <select (change)="this.onCustomerChange($event)" aria-label="Select a customer" class="form-select">
                            <option selected value="">Select a Customer</option>
                            @for (entity of this.customers; track entity.id; ) {
                                <option value="{{ entity.id }}">{{ entity.name }}</option>
                            }
                        </select>
                    </div>

                    <div class="col-sm-auto">
                        <select (change)="this.onTradingPartnerChange($event)" aria-label="Select a trading partner" class="form-select">
                            <option selected value="">Select a Trading Partner</option>
                            @for (name of this.tradingPartnerNames; track name; ) {
                                <option value="{{ name }}">{{ name }}</option>
                            }
                        </select>
                    </div>

                    <div class="input-group col-sm-auto">
                        <span class="input-group-text" id="search-addon"><i class="bi bi-search"></i></span>
                        <input [(ngModel)]="this.ediFileService.searchTerm"
                            (ngModelChange)="this.selectedFiles.clear()" class="form-control shadow-none"
                            id="table-complete-search" name="searchTerm" type="text"
                            placeholder="Search" aria-label="Search"/>

                        @if (this.hasFilter) {
                            <button type="button" class="btn"
                                    [ngClass]="this.isCustomFilterApplied ? 'btn-primary' : 'btn-outline-primary'"
                                    (click)="this.onOpenFilter()">
                                <i class="bi bi-funnel me-1"></i>Filters
                            </button>
                        }
                    </div>

                    <div class="col-sm-auto btn-group" role="group">

                        <a class="btn btn-outline-primary" routerLink="/processing/requests"
                           target="_blank" rel="noopener">
                            <i class="bi bi-clock-history me-2"></i>Request History
                        </a>

                        @if (!!this.customerId) {
                            <button type="button" class="btn btn-outline-danger"
                                    (click)="this.onShowInvalidIdentifiers()">
                                <i class="bi bi-exclamation-triangle me-2"></i>Invalid Identifiers
                            </button>
                        }

                        @if (this.hasFilter && ((this.total$ | async) ?? 0) > 0) {
                            <button type="button" class="btn btn-outline-primary"
                                    (click)="this.exportCsv()">
                                <i class="bi bi-file-earmark-spreadsheet me-2"></i>Export CSV
                            </button>

                            <button type="button" class="btn btn-outline-primary"
                                    (click)="this.onReprocessingRequest()"
                                    [disabled]="!this.selectedFiles.size">
                                <i class="bi bi-repeat me-2"></i>Reprocess
                            </button>

                            <button type="button" class="btn btn-outline-primary"
                                    (click)="this.onBackoutRequest()"
                                    [disabled]="!this.selectedFiles.size">
                                <i class="bi bi-rewind me-2"></i>Back Out
                            </button>
                        }
                    </div>
                </div>
            </form>

            @if (this.hasFilter && ((this.total$ | async) ?? 0) > 0) {
                @if (this.ediFileService.data$ | async; as pageElements) {
                    <table class="table table-striped table-hover">
                        <caption>
                            <div class="container-fluid w-100">
                                <div class="row justify-content-center align-self-center">
                                    <div class="col align-self-center">
                                        {{ this.total$ | async }} Total
                                    </div>
                                </div>
                            </div>
                        </caption>

                        <thead>
                        <tr>
                            <th scope="col">
                                <div class="form-check">
                                    <input class="form-check-input" type="checkbox"
                                           [checked]="isAllOnPageSelected(pageElements)"
                                           (change)="toggleSelectAllOnPage(pageElements)"
                                           aria-label="Select all files on this page">
                                </div>
                            </th>
                            <th (sort)="this.onSort($event)" scope="col" sortable="customerName">Customer</th>
                            <th (sort)="this.onSort($event)" scope="col" sortable="tradingPartnerName">Partner</th>
                            <th (sort)="this.onSort($event)" scope="col" sortable="fileName">File</th>
                            <th scope="col" class="text-end">Lines</th>
                            <th (sort)="this.onSort($event)" scope="col" sortable="loadTime">Load Time</th>
                            <th (sort)="this.onSort($event)" scope="col" sortable="maxReportingDate" class="text-center">Reporting Dates</th>
                            <th scope="col" class="text-center">Maint.</th>
                            <th scope="col" class="text-center">Actions</th>
                        </tr>
                        </thead>

                        <tbody class="table-group-divider">
                            @for (element of pageElements; track element.fileGuid; ) {
                                <tr [ngClass]="element.hasInvalidProducts || element.hasInvalidDistributionCenters ? 'table-danger' : ''" class="align-middle">
                                    <td>
                                        <div class="form-check">
                                            <input class="form-check-input" type="checkbox"
                                                   [checked]="isFileSelected(element.fileGuid)"
                                                   (change)="toggleFileSelection(element)"
                                                   id="checkbox-{{element.fileGuid}}">
                                        </div>
                                    </td>
                                    <td>{{ element.customerName }}</td>
                                    <td>{{ element.tradingPartnerName }}</td>
                                    <td class="text-ellipsis" title="{{ element.fileName }}"
                                        style="max-width: 15em;">{{ element.fileName }}
                                    </td>
                                    <td class="text-end">{{ element.lineCount.toLocaleString('en-US') }}</td>
                                    <td>{{ this.longFormatDate(element.loadTime) }}</td>
                                    <td class="text-center">{{ this.rangeFormatDate(element.minReportingDate, element.maxReportingDate) }}</td>
                                    <td class="text-center">
                                        @if (element.hasInvalidDistributionCenters) {
                                            <i class="bi bi-building-fill text-danger me-2" title="Invalid distribution centers"></i>
                                        }
                                        @if (element.hasInvalidProducts) {
                                            <i class="bi bi-capsule text-danger" title="Invalid products"></i>
                                        }
                                    </td>
                                    <td class="text-center">
                                        <button type="button" class="btn btn-sm btn-outline-primary me-2"
                                                title="Copy file name"
                                                (click)="copyFileName(element.fileName)">
                                            <i class="bi bi-copy"></i>
                                        </button>
                                        <div class="d-inline-block" ngbDropdown #myDrop="ngbDropdown">
                                            <button type="button" class="btn btn-sm btn-outline-primary me-2"
                                                    id="downloadBtn-{{element.fileGuid}}"
                                                    (focus)="myDrop.open()" ngbDropdownAnchor>
                                                <i class="bi bi-download"></i>
                                            </button>
                                            <div ngbDropdownMenu>
                                                <button ngbDropdownItem
                                                        (click)="this.onDownloadFile(element.fileGuid)">
                                                    <i class="bi bi-filetype-raw me-2"></i>Raw
                                                </button>
                                                @if (!element.isLargeFile) {
                                                    <button ngbDropdownItem
                                                            (click)="this.onDownloadCleansedFile(element.fileGuid)">
                                                        <i class="bi bi-file-earmark-text me-2"></i>Cleansed
                                                    </button>
                                                }
                                            </div>
                                        </div>
                                        @if (element.lastReprocessingRequestTime) {
                                            <a class="btn btn-sm btn-outline-primary"
                                               title="Request history"
                                               routerLink="/processing/requests"
                                               [queryParams]="{fileGuid: element.fileGuid}"
                                               target="_blank" rel="noopener">
                                                <i class="bi bi-clock-history"></i>
                                            </a>
                                        }
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            }

            @if (this.hasFilter && ((this.total$ | async) ?? 0) > 0) {
                <app-table-pager [total]="(this.total$ | async)!" [(page)]="this.ediFileService.page" [(pageSize)]="this.ediFileService.pageSize" />
            }

            @if (!this.hasFilter) {
                <div class="card text-center bg-info-subtle border border-info rounded p-3 m-5">
                    <div class="card-body">
                        <i class="bi bi-info-circle-fill me-2"></i>
                        Select a customer or trading partner to see files from the last 30 days.
                    </div>
                </div>
            }

            @if (this.hasFilter && ((this.total$ | async) ?? 0) == 0 && !(this.globalService.waitOverlayIsVisible$ | async)) {
                <div class="card text-center bg-warning-subtle border border-warning rounded p-3 m-5">
                    <div class="card-body">
                        <i class="bi bi-exclamation-triangle-fill me-2"></i>No files found.
                    </div>
                </div>
            }
        </div>
    </div>

    <app-modal-confirm #confirmationModal></app-modal-confirm>

    <ng-template #invalidIdentifiersModal>
        <div class="modal-header">
            <h1 class="modal-title fs-5">Invalid Identifiers</h1>
            <button (click)="onInvalidIdentifiersDismiss()" class="btn-close" type="button"></button>
        </div>
        <div class="modal-body">
            <table class="table table-striped table-hover mb-0">
                <caption>
                    <div class="container-fluid w-100">
                        <div class="row justify-content-center align-self-center">
                            <div class="col align-self-center">
                                {{ this.invalidIdentifiersService.total$ | async }} Total
                            </div>
                        </div>
                    </div>
                </caption>
                <thead>
                <tr>
                    <th (sort)="this.onSortInvalidIdentifier($event)" scope="col" sortable="type" style="min-width: 5em;">Type</th>
                    <th (sort)="this.onSortInvalidIdentifier($event)" scope="col" sortable="name">Partner</th>
                    <th (sort)="this.onSortInvalidIdentifier($event)" scope="col" sortable="qualifier">Qualifier</th>
                    <th (sort)="this.onSortInvalidIdentifier($event)" scope="col" sortable="value">Value</th>
                    <th (sort)="this.onSortInvalidIdentifier($event)" scope="col" sortable="details">Details</th>
                </tr>
                </thead>
                <tbody>
                    @for (ii of this.invalidIdentifiersService.data$ | async; track ii.type + ii.value + (ii.qualifier ?? ''); ) {
                        <tr class="align-middle">
                            <td>{{ ii.type }}</td>
                            <td>{{ ii.name ?? "" }}</td>
                            <td>{{ ii.qualifier ?? ""}}</td>
                            <td>{{ ii.value ?? ""}}</td>
                            <td>{{ ii.details ?? ""}}</td>
                        </tr>
                    }
                </tbody>
            </table>
            @if (((this.invalidIdentifiersService.total$ | async) ?? 0) > 0) {
                <div class="d-flex justify-content-between p-2">
                    <ngb-pagination [(page)]="this.invalidIdentifiersService.page"
                                    [collectionSize]="(this.invalidIdentifiersService.total$ | async)!"
                                    [pageSize]="this.invalidIdentifiersService.pageSize" [ellipses]="true"
                                    [maxSize]="10"></ngb-pagination>

                    <select [(ngModel)]="this.invalidIdentifiersService.pageSize"
                            class="form-select form-select-sm" name="pageSize" style="width: auto">
                        @for (element of this.globalService.pagination; track element; ) {
                            <option [ngValue]="element">
                                {{ element }} items per page
                            </option>
                        }
                    </select>
                </div>
            }
        </div>
        <div class="modal-footer">
            <button (click)="onInvalidIdentifiersDismiss()" class="btn btn-secondary" type="button">Close</button>
        </div>
    </ng-template>

    <app-edi-file-summary-filter
            [customerId]="this.customerId"
            [tradingPartnerName]="this.tradingPartnerName"
            (filterEvent)="this.onFilter($event)"
            (isCustomFilterApplied)="this.onCustomFilterApplied($event)"></app-edi-file-summary-filter>
</div>
